// ============================================================
//  Plan-Rechnung: Zielzustand über die gewählten Maßnahmen und Personenmengen (R4).
//
//  Eine Plan-Position = eine Maßnahme (eine Rolle auf einem System) + Umfang (welche Träger
//  nach Trace dazugehören). Für jede Person werden GENAU die Rollen entfernt, bei denen sie
//  im Umfang einer Plan-Position liegt; die Klasse danach liefert `classWithout` (R2 über
//  alle Systeme und Mandanten, Untergrenze Self-Service). Δ = Gewicht(vorher) − Gewicht(nachher).
//  Eine Person zählt einmal, egal wie viele Positionen sie treffen (R4) — die Summe der
//  Positionsanteile ist deshalb exakt die Gesamtreduktion.
// ============================================================
import type { Lever, LicenseClass, Person, TraceState } from "./types";
import type { Dataset } from "../data/generator";
import { DEFAULT_PRICE_PER_FUE, DEFAULT_RATIO, type RatioTable, weight } from "./rules";
import { totals } from "./aggregate";
import { contractedFue } from "../store/contract";
import { classWithout, roleIndex } from "./analysis";
import type { PlanItem, TraceScope } from "../store/plan";
import { su01Of } from "./su01";
import { TRACE_SEVERITY } from "./trace";

// ------------------------------------------------------------ Umfang (Trace)
/** Welche Trace-Zustände der jeweilige Umfang einschließt. */
export const SCOPE_TRACES: Record<TraceScope, readonly TraceState[]> = {
  nie: ["nie-genutzt"],
  "nie+genutzt": ["nie-genutzt", "genutzt"],
  alle: ["nie-genutzt", "genutzt", "kein-trace"],
};
export function inScope(trace: TraceState, scope: TraceScope): boolean {
  return SCOPE_TRACES[scope].includes(trace);
}

// Trace je Person = strengster Befund über alle ihre Konten — gemeinsame Regel in trace.ts
// (gilt auch für die Maßnahmen in build.ts und die Aufschlüsselung in analysis.ts).
export { personTraceFor } from "./trace";

/** Split-Maßnahme: die Rolle wird geteilt — kleine Rolle ohne den Wert, große Rolle mit dem Wert. */
export function isSplitLever(lv: Pick<Lever, "action">): boolean {
  return /splitten|neu schneiden/i.test(lv.action);
}

// ------------------------------------------------------------ Ergebnistypen
/** Eine Person in einer Personenliste — mit Klasse vorher → nachher. */
export interface PlanPersonRow {
  id: string;
  name?: string;
  /** Funktion aus den SU01-Adressfeldern der Konten (häufigster gepflegter Wert). */
  funktion?: string;
  department?: string;
  unit: string;
  from: LicenseClass;
  to: LicenseClass;
  /** Gewicht(vorher) − Gewicht(nachher) im Zielzustand (0, wenn die Person oben bleibt). */
  delta: number;
  /** Trace dieser Person für diese Maßnahme (strengster Befund über ihre Konten). */
  trace: TraceState;
  /** true: ohne diese Position wäre Δ dieser Person kleiner — die Position wird gebraucht. */
  needed: boolean;
}

/** Eine Person im Umfang, die trotz Plan oben bleibt — und wer sie hält. */
export interface PlanHeldRow {
  id: string;
  name?: string;
  funktion?: string;
  department?: string;
  unit: string;
  cls: LicenseClass;
  trace: TraceState;
  holderSid: string;
  holderRoleId: string;
  holderRoleName: string;
  /** Maßnahme, die den Halter lösen würde (nur wenn es sie gibt). */
  holderLeverId?: string;
  /** true: die Halter-Maßnahme existiert, liegt aber nicht im Plan. */
  holderMissing: boolean;
  /** true: die Halter-Maßnahme liegt im Plan, aber diese Person ist dort außerhalb des Umfangs (Trace). */
  holderOutOfScope: boolean;
}

export interface PlanTransition { fromClass: LicenseClass; toClass: LicenseClass; persons: number; fue: number }

export interface PlanItemResult {
  leverId: string;
  scope: TraceScope;
  /** Personen, deren Δ > 0 diese Position braucht (ohne sie wäre Δ kleiner). */
  effective: number;
  /** Personen im Umfang (Träger, deren Trace zum Umfang passt). */
  inScope: number;
  /** Träger der Maßnahme insgesamt (im und außerhalb des Umfangs). */
  carriers: number;
  /** Personen im Umfang, die im Zielzustand oben bleiben. */
  held: number;
  /**
   * Anteil dieser Position an der Gesamtreduktion: Δ jeder fallenden Person geteilt durch die
   * Zahl der Positionen, die sie dafür braucht. Σ deltaFue über alle Positionen = reduction.
   */
  deltaFue: number;
  /** Δ, wenn NUR diese Position (mit ihrem Umfang) umgesetzt würde — sonst nichts. */
  alone: number;
  /** Was der Plan ohne diese Position verlieren würde: reduction(Plan) − reduction(Plan ∖ Position). */
  marginal: number;
  /** Zielzustand-Δ aller Personen im Umfang dieser Position (Personen können in mehreren Positionen liegen). */
  withPlan: number;
  transitions: PlanTransition[];
  /** Personen im Umfang je Trace; fue = Δ der davon fallenden Personen. */
  byTrace: Record<TraceState, { persons: number; fue: number }>;
  /** Personen mit Δ > 0 im Umfang (Objekte, für Verknüpfungen). */
  persons: Person[];
  fallen: PlanPersonRow[];
  bleibenOben: PlanHeldRow[];
  isSplit: boolean;
  /** Split: Personen im Umfang — sie erhalten die Rolle ohne den Wert. */
  kleineRolle: PlanPersonRow[];
  /** Split: Träger außerhalb des Umfangs — sie behalten die Rolle mit dem Wert. */
  grosseRolle: PlanPersonRow[];
}

/** Halter-Maßnahme, die im Plan fehlt — und wie viele Personen deshalb oben bleiben. */
export interface MissingCombo {
  leverId: string;
  roleId: string;
  roleName: string;
  sid: string;
  persons: number;
  /** Plan-Positionen, deren Personen davon betroffen sind. */
  viaItems: string[];
}

export interface PlanResult {
  reduction: number;   // FUE im Zielzustand gegenüber heute
  euroSaved: number;   // reduction × Preis
  persons: number;     // Personen, die sich abstufen (jede einmal)
  afterPlan: number;   // Bedarf nach Plan
  restLuecke: number;  // afterPlan − Vertrag (negativ = Puffer)
  ist: number;         // Bedarf heute
  contracted: number;  // Vertrag
  items: number;       // Positionen (ohne Dubletten, ohne unbekannte Maßnahmen)
  perItem: Record<string, PlanItemResult>;
  missingCombos: MissingCombo[];
}

// ------------------------------------------------------------ Rechnung
const roleKey = (roleId: string, sid: string) => `${roleId}|${sid}`;

function emptyByTrace(): Record<TraceState, { persons: number; fue: number }> {
  return { "nie-genutzt": { persons: 0, fue: 0 }, genutzt: { persons: 0, fue: 0 }, "kein-trace": { persons: 0, fue: 0 } };
}

function rowBase(p: Person, from: LicenseClass, to: LicenseClass, delta: number, trace: TraceState, needed: boolean): PlanPersonRow {
  return { id: p.id, name: p.name, department: p.department, unit: p.unit, from, to, delta, trace, needed };
}

export function planResult(ds: Dataset, items: PlanItem[], ratio: RatioTable = DEFAULT_RATIO, price: number = DEFAULT_PRICE_PER_FUE): PlanResult {
  const leverById = new Map(ds.levers.map((l) => [l.id, l]));
  const accById = new Map(ds.accounts.map((a) => [a.id, a]));
  const suCache = new Map<string, string | undefined>();
  const funktionOf = (p: Person) => { if (!suCache.has(p.id)) suCache.set(p.id, su01Of(p, accById).funktion); return suCache.get(p.id); };
  const row = (p: Person, from: LicenseClass, to: LicenseClass, delta: number, trace: TraceState, needed: boolean): PlanPersonRow => ({ ...rowBase(p, from, to, delta, trace, needed), funktion: funktionOf(p) });
  // Positionen: je Maßnahme genau eine (erste gewinnt), nur Maßnahmen, die es gibt.
  const plan = new Map<string, PlanItem>();
  for (const it of items) if (!plan.has(it.leverId) && leverById.has(it.leverId)) plan.set(it.leverId, it);

  const ix = roleIndex(ds);
  const leverIds = new Set(ds.levers.map((l) => l.id));
  const keyOfLever = new Map<string, string>();
  const perItem: Record<string, PlanItemResult> = {};
  for (const [id, it] of plan) {
    const lv = leverById.get(id)!;
    keyOfLever.set(id, roleKey(lv.roleId, lv.sid));
    perItem[id] = {
      leverId: id, scope: it.scope, effective: 0, inScope: 0, carriers: 0, held: 0,
      deltaFue: 0, alone: 0, marginal: 0, withPlan: 0, transitions: [], byTrace: emptyByTrace(),
      persons: [], fallen: [], bleibenOben: [], isSplit: isSplitLever(lv), kleineRolle: [], grosseRolle: [],
    };
  }
  const transMaps = new Map<string, Map<string, PlanTransition>>();
  for (const id of plan.keys()) transMaps.set(id, new Map());
  const missing = new Map<string, { combo: MissingCombo; persons: Set<string>; via: Set<string> }>();

  let reduction = 0, persons = 0;
  if (plan.size > 0) {
    for (const p of ds.persons) {
      // Trace je Plan-Maßnahme (strengster Befund über die Konten der Person).
      let traces: Map<string, TraceState> | null = null;
      for (const d of p.drivers) {
        if (!d.leverId || !plan.has(d.leverId)) continue;
        traces ??= new Map();
        const have = traces.get(d.leverId);
        if (have === undefined || TRACE_SEVERITY[d.trace] > TRACE_SEVERITY[have]) traces.set(d.leverId, d.trace);
      }
      if (!traces) continue;

      const cur = weight(p.licenseClass, ratio);
      const inScopeIds: string[] = [];
      const dropSet = new Set<string>();
      for (const [lid, t] of traces) {
        const r = perItem[lid];
        r.carriers++;
        if (inScope(t, plan.get(lid)!.scope)) { inScopeIds.push(lid); dropSet.add(keyOfLever.get(lid)!); r.inScope++; r.byTrace[t].persons++; }
        else if (r.isSplit) r.grosseRolle.push(row(p, p.licenseClass, p.licenseClass, 0, t, false));
      }
      if (inScopeIds.length === 0) continue;

      const after = classWithout(p, ix, (r, s) => dropSet.has(roleKey(r, s)));
      const delta = cur - weight(after.cls, ratio);

      if (delta > 0) {
        reduction += delta; persons++;
        // Welche Positionen braucht diese Person? Ohne die Position wäre Δ kleiner.
        const single = inScopeIds.length === 1;
        const needed: string[] = [];
        const marginalOf = new Map<string, number>();
        const aloneOf = new Map<string, number>();
        for (const lid of inScopeIds) {
          if (single) { marginalOf.set(lid, delta); aloneOf.set(lid, delta); continue; }
          const key = keyOfLever.get(lid)!;
          const ohne = classWithout(p, ix, (r, s) => { const k = roleKey(r, s); return k !== key && dropSet.has(k); });
          marginalOf.set(lid, delta - (cur - weight(ohne.cls, ratio)));
          const nur = classWithout(p, ix, (r, s) => roleKey(r, s) === key);
          aloneOf.set(lid, cur - weight(nur.cls, ratio));
        }
        for (const lid of inScopeIds) if (marginalOf.get(lid)! > 1e-9) needed.push(lid);
        const share = needed.length ? delta / needed.length : 0;
        for (const lid of inScopeIds) {
          const r = perItem[lid];
          const t = traces.get(lid)!;
          const isNeeded = needed.includes(lid);
          const pr = row(p, p.licenseClass, after.cls, delta, t, isNeeded);
          r.fallen.push(pr); r.persons.push(p);
          if (r.isSplit) r.kleineRolle.push(pr);
          r.withPlan += delta; r.byTrace[t].fue += delta;
          r.marginal += marginalOf.get(lid)!; r.alone += aloneOf.get(lid)!;
          if (isNeeded) { r.effective++; r.deltaFue += share; }
          const tk = `${p.licenseClass}>${after.cls}`;
          const tm = transMaps.get(lid)!;
          const u = tm.get(tk) ?? { fromClass: p.licenseClass, toClass: after.cls, persons: 0, fue: 0 };
          u.persons++; u.fue += delta; tm.set(tk, u);
        }
      } else {
        // Bleibt oben: wer hält? (Halter kann nie eine entfernte Rolle sein.)
        const h = after.holder;
        for (const lid of inScopeIds) {
          const r = perItem[lid];
          const t = traces.get(lid)!;
          r.held++;
          if (r.isSplit) r.kleineRolle.push(row(p, p.licenseClass, after.cls, 0, t, false));
          // allein: auch allein fällt sie nicht (der Plan entfernt eine Obermenge der Rollen).
          if (!h) continue;
          const holderLeverId = h.roleId === "#DEV#" ? undefined : (() => { const l = ix.leverOf.get(h.roleId); return l && leverIds.has(l) ? l : undefined; })();
          const holderInPlan = !!holderLeverId && plan.has(holderLeverId);
          const hr: PlanHeldRow = {
            id: p.id, name: p.name, funktion: funktionOf(p), department: p.department, unit: p.unit, cls: p.licenseClass, trace: t,
            holderSid: h.sid, holderRoleId: h.roleId,
            holderRoleName: h.roleId === "#DEV#" ? "Developer-Kennzeichen" : (ix.nameOf.get(h.roleId) ?? h.roleId),
            holderLeverId, holderMissing: !!holderLeverId && !holderInPlan, holderOutOfScope: holderInPlan,
          };
          r.bleibenOben.push(hr);
          if (hr.holderMissing) {
            const lv = leverById.get(holderLeverId!)!;
            const m = missing.get(holderLeverId!) ?? { combo: { leverId: holderLeverId!, roleId: lv.roleId, roleName: lv.roleName, sid: lv.sid, persons: 0, viaItems: [] }, persons: new Set<string>(), via: new Set<string>() };
            m.persons.add(p.id); m.via.add(lid); missing.set(holderLeverId!, m);
          }
        }
      }
    }
  }

  for (const [id, tm] of transMaps) perItem[id].transitions = [...tm.values()].sort((a, b) => b.persons - a.persons);
  const missingCombos = [...missing.values()]
    .map((m) => ({ ...m.combo, persons: m.persons.size, viaItems: [...m.via] }))
    .sort((a, b) => b.persons - a.persons);

  const ist = totals(ds.persons, ratio).ist;
  const afterPlan = ist - reduction;
  return {
    reduction, euroSaved: reduction * price, persons, afterPlan, restLuecke: afterPlan - contractedFue(ratio),
    ist, contracted: contractedFue(ratio), items: plan.size, perItem, missingCombos,
  };
}

/** Hilfsfunktion für Dialoge: eine hypothetische Position ohne Store-Eintrag. */
export function hypotheticalItem(leverId: string, scope: TraceScope): PlanItem {
  return { leverId, scope, addedAt: 0, ticket: false };
}
